import { Menu, Popover } from 'antd';
import { useSelector, useDispatch } from 'react-redux';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useEffect, useState, useRef } from 'react';
import MenuItems from '@/menu-items/index';
import { activeItem, openDrawer } from '@/store/reducers/menu';
import { getSidebarColor } from '@/themes/antd-theme';
import { useTheme } from '@/App';

// Popover content for collapsed menu items with children
const CollapsedMenuPopover = ({ item, selectedKeys, onNavigate, onClose }) => {
  const renderChildren = (children, level = 0) => {
    return children
      ?.filter((child) => child.showMenu !== false)
      .map((child) => {
        const isSelected = selectedKeys.includes(child.id);
        const Icon = child.icon;

        // If child has its own children (nested collapse)
        if (child.type === 'collapse' && child.children) {
          return (
            <div key={child.id} className="popover-submenu-group">
              <div className="popover-submenu-title">
                {Icon && <Icon style={{ fontSize: '14px' }} />}
                <span>{child.title}</span>
              </div>
              <div className="popover-submenu-items">
                {renderChildren(child.children, level + 1)}
              </div>
            </div>
          );
        }

        return (
          <div
            key={child.id}
            className={`popover-menu-item ${isSelected ? 'selected' : ''}`}
            onClick={() => {
              onNavigate(child);
              onClose();
            }}
          >
            {Icon && <Icon style={{ fontSize: '16px' }} />}
            <span>{child.title}</span>
          </div>
        );
      });
  };

  return (
    <div className="collapsed-menu-popover">
      <div className="popover-title">{item.title}</div>
      <div className="popover-items">{renderChildren(item.children)}</div>
    </div>
  );
};

// Helper function to find active item (defined outside component to avoid recreating)
const findActiveItem = (items, path) => {
  for (const item of items) {
    if (item.url === path) {
      return item.id;
    }
    if (item.children) {
      const found = findActiveItem(item.children, path);
      if (found) return found;
    }
  }
  return null;
};

// Walks the menu tree collecting the ids of every ancestor collapse/group
// that contains targetId, so its submenu can be auto-expanded to reveal it.
const findParentKeys = (items, targetId, parents = []) => {
  for (const item of items) {
    if (item.id === targetId) {
      return parents;
    }
    if (item.children) {
      const found = findParentKeys(item.children, targetId, [...parents, item.id]);
      if (found.length > 0 || item.children.some((child) => child.id === targetId)) {
        return [...parents, item.id];
      }
    }
  }
  return [];
};

// ==============================|| DRAWER CONTENT - NAVIGATION ||============================== //

const Navigation = () => {
  const menuItem = MenuItems();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { drawerOpen, openItem } = useSelector((state) => state.menu);
  const { theme_color } = useSelector((state) => state.company_profile);
  const { isDark } = useTheme();
  const [openKeys, setOpenKeys] = useState([]);
  const [openPopover, setOpenPopover] = useState(null);
  const lastPathRef = useRef(pathname);

  // Get popover color based on dark/light mode
  const popoverBgColor = getSidebarColor(theme_color, isDark);

  // Handle navigation from popover
  const handlePopoverNavigate = (item) => {
    if (item.external) {
      const targetValue = item.target === true ? '_blank' : item.target || '_self';
      window.open(item.url, targetValue);
    } else if (item.url) {
      navigate(item.url);
      dispatch(activeItem({ openItem: [item.id] }));
      localStorage.setItem('item', JSON.stringify({ id: item.id }));
    }
  };

  // Update selected item based on current path - only when pathname actually changes
  useEffect(() => {
    if (lastPathRef.current !== pathname) {
      lastPathRef.current = pathname;
      const allItems = menuItem.items.flatMap((group) => group.children || []);
      const activeId = findActiveItem(allItems, pathname);
      if (activeId) {
        dispatch(activeItem({ openItem: [activeId] }));
      }
    }
  }, [pathname, dispatch, menuItem.items]);

  // Whenever the sidebar overlay opens, auto-expand the submenu chain leading
  // to the currently active page so it's visibly shown as selected right away,
  // instead of staying hidden inside a collapsed submenu.
  useEffect(() => {
    if (drawerOpen && openItem?.[0]) {
      const parents = findParentKeys(menuItem.items, openItem[0]);
      if (parents.length) {
        setOpenKeys((prev) => Array.from(new Set([...prev, ...parents])));
      }
    }
  }, [drawerOpen, openItem, menuItem.items]);

  // Convert menu items to Ant Design format
  const convertToAntdItems = (items, level = 1, isInsideGroup = false) => {
    return items
      ?.filter((item) => item.showMenu !== false)
      .map((item) => {
        const Icon = item.icon;
        const iconElement = Icon ? <Icon style={{ fontSize: '1rem' }} /> : null;

        if (item.type === 'group') {
          // When drawer is collapsed, don't wrap in group - just return children directly
          // Each child will show its own icon
          if (!drawerOpen) {
            return convertToAntdItems(item.children, level, true);
          }

          // When drawer is open, use normal group
          return {
            key: item.id,
            type: 'group',
            label: item.title ? (
              <span className="text-xs font-medium text-gray-500 uppercase tracking-wider pl-2">
                {item.title}
              </span>
            ) : null,
            children: convertToAntdItems(item.children, level, true),
          };
        }

        if (item.type === 'collapse') {
          // When drawer is collapsed, use Popover instead of submenu
          if (!drawerOpen) {
            const popoverContent = (
              <CollapsedMenuPopover
                item={item}
                selectedKeys={openItem}
                onNavigate={handlePopoverNavigate}
                onClose={() => setOpenPopover(null)}
              />
            );

            return {
              key: item.id,
              icon: (
                <Popover
                  content={popoverContent}
                  placement="right"
                  trigger="hover"
                  open={openPopover === item.id}
                  onOpenChange={(visible) => setOpenPopover(visible ? item.id : null)}
                  rootClassName="collapsed-nav-popover"
                  arrow={false}
                  mouseEnterDelay={0.1}
                  mouseLeaveDelay={0.2}
                >
                  <span className="popover-icon-wrapper">{iconElement}</span>
                </Popover>
              ),
              label: null,
              title: null,
              className: 'collapsed-popover-item',
            };
          }

          // When drawer is open, use normal submenu
          const childrenItems = convertToAntdItems(item.children, level + 1, false);
          return {
            key: item.id,
            icon: iconElement,
            label: item.title,
            children: childrenItems,
            popupClassName: 'nav-popup-menu',
          };
        }

        if (item.type === 'item') {
          // Ensure target is a valid string value
          const targetValue = item.target === true ? '_blank' : item.target || '_self';

          // When drawer is collapsed, make the icon itself clickable with a link wrapper
          const clickableIcon =
            !drawerOpen && iconElement ? (
              item.external ? (
                <a
                  href={item.url}
                  target={targetValue}
                  className="flex items-center justify-center w-full h-full"
                >
                  {iconElement}
                </a>
              ) : (
                <Link to={item.url} className="flex items-center justify-center w-full h-full">
                  {iconElement}
                </Link>
              )
            ) : (
              iconElement
            );

          return {
            key: item.id,
            icon: clickableIcon,
            label: item.external ? (
              <a href={item.url} target={targetValue}>
                {item.title}
              </a>
            ) : (
              <Link to={item.url}>{item.title}</Link>
            ),
            // Suppress antd Menu's own built-in collapsed-item hover tooltip
            // entirely — unnecessary noise when just glancing at the collapsed rail.
            title: !drawerOpen ? null : undefined,
          };
        }

        return null;
      })
      .filter(Boolean);
  };

  const menuItems = convertToAntdItems(menuItem.items).flat();

  const handleClick = (e) => {
    dispatch(activeItem({ openItem: [e.key] }));
    localStorage.setItem('item', JSON.stringify({ id: e.key }));
    // Selecting a destination closes the overlay back to the collapsed rail
    // so the page underneath is immediately visible at full width.
    if (drawerOpen) dispatch(openDrawer({ drawerOpen: false }));
  };

  const handleOpenChange = (keys) => {
    setOpenKeys(keys);
  };

  return (
    <>
      <style>
        {`
          /* Remove SimpleBar and Sider internal padding */
          .simplebar-content,
          .simplebar-content-wrapper {
            padding-top: 0 !important;
            margin-top: 0 !important;
          }
          .ant-layout-sider-children {
            padding-top: 0 !important;
            margin-top: 0 !important;
          }

          /* Collapse empty group title divs — no title text = no space */
          .nav-menu .ant-menu-item-group-title:has(> span:empty),
          .nav-menu .ant-menu-item-group-title:empty {
            display: none !important;
            padding: 0 !important;
            margin: 0 !important;
            height: 0 !important;
          }

          /* Remove top padding from menu */
          .nav-menu {
            padding-top: 0 !important;
            padding-bottom: 0 !important;
            margin-top: 0 !important;
          }
          .nav-menu .ant-menu-item:first-child,
          .nav-menu > .ant-menu-item-group:first-child,
          .nav-menu > .ant-menu-submenu:first-child {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          /* Group title styling - reduce space */
          .nav-menu .ant-menu-item-group-title {
            padding: 4px 16px !important;
            margin-top: 4px !important;
            margin-bottom: 0 !important;
          }
          .nav-menu > .ant-menu-item-group:first-child > .ant-menu-item-group-title {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }
          .nav-menu .ant-menu-item-group-list {
            margin-top: 0 !important;
            padding-top: 0 !important;
          }

          /* Normal expanded menu styles - pure white text with increased font size */
          .nav-menu .ant-menu-item,
          .nav-menu .ant-menu-submenu-title {
            display: flex !important;
            align-items: center !important;
            gap: 12px !important;
            font-size: 15px !important;
            color: #ffffff !important;
            /* Match the sidebar's own 0.3s width animation so the row's own
               width/padding resize (collapsed <-> expanded) isn't an instant jump. */
            transition: width 0.4s cubic-bezier(0.4, 0, 0.2, 1), padding 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          }
          .nav-menu .ant-menu-item .ant-menu-item-icon,
          .nav-menu .ant-menu-submenu-title .ant-menu-item-icon {
            margin-inline-end: 0 !important;
            min-width: 20px !important;
            font-size: 20px !important;
            color: #ffffff !important;
          }
          .nav-menu .ant-menu-item .ant-menu-title-content,
          .nav-menu .ant-menu-submenu-title .ant-menu-title-content {
            margin-inline-start: 0 !important;
            color: #ffffff !important;
            font-size: 15px !important;
            /* Fades/shrinks in step with the sidebar's own 0.3s width animation,
               instead of the label popping in/out in a single frame. */
            transition: opacity 0.4s cubic-bezier(0.4, 0, 0.2, 1), width 0.4s cubic-bezier(0.4, 0, 0.2, 1);
            opacity: 1;
          }
          .nav-menu .ant-menu-item .ant-menu-title-content a,
          .nav-menu .ant-menu-submenu-title .ant-menu-title-content a {
            color: #ffffff !important;
          }

          /* Selected item styles - white background with blue text */
          .nav-menu .ant-menu-item-selected {
            background-color: #ffffff !important;
          }
          .nav-menu .ant-menu-item-selected,
          .nav-menu .ant-menu-item-selected .ant-menu-item-icon,
          .nav-menu .ant-menu-item-selected .ant-menu-title-content,
          .nav-menu .ant-menu-item-selected .ant-menu-title-content a {
            color: #1890ff !important;
          }
          /* Selected item left border indicator */
          .nav-menu .ant-menu-item-selected::after {
            border-right-color: #1890ff !important;
          }
          /* Parent/collapsed submenu title - pure white text */
          .nav-menu .ant-menu-submenu-title,
          .nav-menu .ant-menu-submenu-title .ant-menu-item-icon,
          .nav-menu .ant-menu-submenu-title .ant-menu-title-content {
            color: #ffffff !important;
          }
          .nav-menu .ant-menu-submenu-open > .ant-menu-submenu-title,
          .nav-menu .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-item-icon,
          .nav-menu .ant-menu-submenu-open > .ant-menu-submenu-title .ant-menu-title-content {
            color: #ffffff !important;
          }
          .nav-menu .ant-menu-submenu-arrow {
            color: #ffffff !important;
          }

          /* Collapsed mini sidebar styles - show only icons */
          .nav-menu.ant-menu-inline-collapsed {
            width: 80px !important;
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-title {
            padding: 0 !important;
            justify-content: center !important;
            width: 80px !important;
            height: 44px !important;
            margin: 4px 0 !important;
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item .ant-menu-item-icon,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-title .ant-menu-item-icon {
            font-size: 20px !important;
            margin: 0 !important;
            line-height: 44px !important;
          }
          /* Hide text labels when collapsed — opacity/width only (no display:
             none, which can't be transitioned) so this animates smoothly
             alongside the sidebar's width change instead of snapping instantly. */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-title-content {
            opacity: 0 !important;
            width: 0 !important;
            overflow: hidden !important;
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item-group-title {
            display: none !important;
          }
          /* Hide submenu arrow when collapsed */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-arrow {
            display: none !important;
          }
          /* Center icon in collapsed mode */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item > .ant-menu-item-icon,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu > .ant-menu-submenu-title > .ant-menu-item-icon {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }
          /* Make icon links clickable in collapsed mode */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item .ant-menu-item-icon a,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item > .ant-menu-item-icon > a {
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            width: 100% !important;
            height: 100% !important;
            color: inherit !important;
          }
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item {
            cursor: pointer !important;
          }
          /* Hover effect on collapsed menu items */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-item:hover,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-title:hover {
            background-color: rgba(255, 255, 255, 0.1) !important;
            border-radius: 8px !important;
          }
          /* Active submenu highlight when popup is open */
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-open > .ant-menu-submenu-title,
          .nav-menu.ant-menu-inline-collapsed .ant-menu-submenu-active > .ant-menu-submenu-title {
            background-color: rgba(255, 255, 255, 0.15) !important;
            border-radius: 8px !important;
          }

          /* Popup submenu styles when collapsed - appears on right side */
          .ant-menu-submenu-popup,
          .nav-popup-menu {
            z-index: 9999 !important;
          }
          /* Any Ant Design Tooltip elsewhere in the app — the <nav> wrapper
             around the Drawer sits at z-index: 1300 (Drawer/index.js), which
             is above antd's default Tooltip z-index (~1070), so without this
             a tooltip renders clipped behind the sidebar instead of floating
             above it. */
          .ant-tooltip {
            z-index: 9999 !important;
          }
          .ant-menu-submenu-popup .ant-menu,
          .nav-popup-menu .ant-menu {
            min-width: 220px !important;
            max-width: 300px !important;
            border-radius: 10px !important;
            box-shadow: 0 8px 24px 0 rgba(0, 0, 0, 0.15), 0 4px 8px -4px rgba(0, 0, 0, 0.1) !important;
            background: #fff !important;
            padding: 8px 0 !important;
            overflow: hidden !important;
          }
          /* Popup menu title/header styling */
          .ant-menu-submenu-popup .popup-menu-title,
          .nav-popup-menu .popup-menu-title {
            padding: 10px 16px 8px !important;
            font-size: 13px !important;
            font-weight: 600 !important;
            color: #204448 !important;
            border-bottom: 1px solid #f0f0f0 !important;
            margin-bottom: 4px !important;
            cursor: default !important;
            background: transparent !important;
            pointer-events: none !important;
            height: auto !important;
            line-height: 1.4 !important;
          }
          .ant-menu-submenu-popup .popup-menu-title:hover,
          .nav-popup-menu .popup-menu-title:hover {
            background: transparent !important;
          }
          .ant-menu-submenu-popup .ant-menu-item,
          .nav-popup-menu .ant-menu-item {
            display: flex !important;
            align-items: center !important;
            gap: 10px !important;
            height: 40px !important;
            line-height: 40px !important;
            margin: 4px 8px !important;
            border-radius: 6px !important;
            color: #333 !important;
            padding: 0 12px !important;
          }
          .ant-menu-submenu-popup .ant-menu-item:hover,
          .nav-popup-menu .ant-menu-item:hover {
            background-color: #f5f5f5 !important;
            color: #204448 !important;
          }
          .ant-menu-submenu-popup .ant-menu-item-selected,
          .nav-popup-menu .ant-menu-item-selected {
            background-color: #e6f7ff !important;
            color: #1890ff !important;
            font-weight: 500 !important;
            border-left: 3px solid #1890ff !important;
          }
          .ant-menu-submenu-popup .ant-menu-item-selected a,
          .nav-popup-menu .ant-menu-item-selected a {
            color: #1890ff !important;
            font-weight: 500 !important;
          }
          .ant-menu-submenu-popup .ant-menu-item-selected:hover,
          .nav-popup-menu .ant-menu-item-selected:hover {
            background-color: #bae7ff !important;
            color: #1890ff !important;
          }
          .ant-menu-submenu-popup .ant-menu-item a,
          .nav-popup-menu .ant-menu-item a {
            color: inherit !important;
          }
          .ant-menu-submenu-popup .ant-menu-submenu-title,
          .nav-popup-menu .ant-menu-submenu-title {
            display: flex !important;
            align-items: center !important;
            gap: 10px !important;
            height: 40px !important;
            margin: 4px 8px !important;
            border-radius: 6px !important;
            padding: 0 12px !important;
          }
          .ant-menu-submenu-popup .ant-menu-submenu-title:hover,
          .nav-popup-menu .ant-menu-submenu-title:hover {
            background-color: #f5f5f5 !important;
          }
          /* Nested submenu in popup */
          .ant-menu-submenu-popup .ant-menu-submenu > .ant-menu,
          .nav-popup-menu .ant-menu-submenu > .ant-menu {
            margin-left: 4px !important;
          }

          /* ===== Custom Popover Styles for Collapsed Sidebar ===== */
          .collapsed-nav-popover .ant-popover-inner {
            padding: 0 !important;
            border-radius: 10px !important;
            box-shadow: 0 8px 24px rgba(0, 0, 0, 0.3) !important;
            overflow: hidden !important;
            background: ${popoverBgColor} !important;
          }

          .collapsed-menu-popover {
            min-width: 200px;
            max-width: 280px;
            background: ${popoverBgColor};
          }

          .popover-title {
            padding: 12px 16px 10px;
            font-size: 14px;
            font-weight: 600;
            color: #ffffff;
            border-bottom: 1px solid rgba(255, 255, 255, 0.1);
            background: rgba(0, 0, 0, 0.1);
          }

          .popover-items {
            padding: 8px 0;
          }

          .popover-menu-item {
            display: flex;
            align-items: center;
            gap: 12px;
            padding: 10px 16px;
            cursor: pointer;
            color: #ffffff;
            font-size: 14px;
            transition: all 0.2s ease;
          }

          .popover-menu-item:hover {
            background-color: rgba(255, 255, 255, 0.1);
            color: #ffffff;
          }

          .popover-menu-item.selected {
            background-color: #ffffff !important;
            color: #1890ff !important;
            font-weight: 500;
            border-left: 3px solid #1890ff;
          }

          .popover-menu-item.selected svg {
            color: #1890ff !important;
          }

          /* Nested submenu in popover */
          .popover-submenu-group {
            margin-top: 8px;
          }

          .popover-submenu-title {
            display: flex;
            align-items: center;
            gap: 8px;
            padding: 6px 16px;
            font-size: 12px;
            font-weight: 600;
            color: rgba(255, 255, 255, 0.6);
            text-transform: uppercase;
          }

          .popover-submenu-items .popover-menu-item {
            padding-left: 28px;
          }

          /* Icon wrapper for popover trigger */
          .popover-icon-wrapper {
            display: flex;
            align-items: center;
            justify-content: center;
            width: 100%;
            height: 100%;
            cursor: pointer;
          }

          /* Collapsed popover menu item styling */
          .nav-menu.ant-menu-inline-collapsed .collapsed-popover-item {
            padding: 0 !important;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
          }

          .nav-menu.ant-menu-inline-collapsed .collapsed-popover-item .ant-menu-item-icon {
            margin: 0 !important;
          }
        `}
      </style>
      <Menu
        mode="inline"
        selectedKeys={openItem}
        openKeys={drawerOpen ? openKeys : []}
        onOpenChange={handleOpenChange}
        onClick={handleClick}
        inlineCollapsed={!drawerOpen}
        items={menuItems}
        className="nav-menu border-none bg-transparent"
        style={{
          border: 'none',
          background: 'transparent',
        }}
        triggerSubMenuAction={drawerOpen ? 'click' : 'hover'}
      />
    </>
  );
};

export default Navigation;
